import { Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetProductsWithMedicationsQuery } from "src/api/services/productsApi";
import ProductItems from "src/components/ProductItems";
import { usePatient } from "src/context/PatientProvider";
import { useFetchCarouselProducts } from "src/hooks/useFetchCarouselProducts";
import { usePatientAge } from "src/hooks/usePatientAge";
import CarouselSkeleton from "src/shared/components/CarouselSkeleton";
import ProductCarousel from "src/shared/components/ProductCarousel";

interface Props {
  productCategory: string;
}

const Products = ({ productCategory }: Props) => {
  const { t } = useTranslation();
  const component = "shop.products";
  const gender = usePatient((p) => p.profile.gender);
  const { data = [], isLoading } = useGetProductsWithMedicationsQuery(
    {
      gender: gender as string,
      category: productCategory,
    },
    { skip: !gender },
  );
  const { subscriptionProducts, oneTimeProducts } =
    useFetchCarouselProducts(data);

  const products = [...subscriptionProducts, ...oneTimeProducts];
  const age = usePatientAge();

  const header = useMemo(() => {
    if (productCategory === "Birth Control" && age > 55) {
      return t(`${component}.Menopause.header`, "Menopause Care");
    }

    if (productCategory === "Hair Loss") {
      return t(`product-category.${productCategory}.${gender}`);
    }

    return t(`product-category.${productCategory}`);
  }, [age, gender, productCategory, t]);

  const subtitle = useMemo(() => {
    if (productCategory === "Birth Control" && age > 55) {
      return t(
        `${component}.Menopause.subtitle`,
        "Affordable, doctor-approved Menopausal Care prescribed online and shipped discreetly to your door.",
      );
    }

    if (productCategory === "Hair Loss") {
      return t(`${component}.${productCategory}.${gender}.subtitle`);
    }

    return t(`${component}.${productCategory}.subtitle`);
  }, [age, gender, productCategory, t]);

  return isLoading ? (
    <CarouselSkeleton />
  ) : products.length === 0 ? null : (
    <Stack>
      <Stack gap={1}>
        <Typography variant="h6" fontWeight={700}>
          {header}
        </Typography>
        <Typography variant="subtitle1">{subtitle}</Typography>
      </Stack>
      <ProductCarousel width="100%">
        <ProductItems products={products} />
      </ProductCarousel>
    </Stack>
  );
};

export default Products;
